import React, { useEffect, useState } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import "./AddScopeCatalog.css";

const ScopeCatalog = () => {
  const [scopes, setScopes] = useState([]);
  const [industryMap, setIndustryMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  const navigate = useNavigate();

  useEffect(() => {
    fetchIndustries();
    fetchScopes();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);

  const fetchIndustries = async () => {
    try {
      const res = await axios.get(`${API_BASE}/reference-values/`);
      const allValues = res.data?.data || [];

      const industries = allValues.filter(
        (value) =>
          value.option_set === "INDUSTRY" ||
          value.option_set_value?.option_type === "INDUSTRY"
      );

      const map = {};

      industries.forEach((industry) => {
        map[industry.id] = industry.label;
      });

      setIndustryMap(map);
    } catch (error) {
      console.error("Error fetching industries:", error);
    }
  };

  const fetchScopes = async () => {
    setLoading(true);

    try {
      const res = await axios.get(`${API_BASE}/scope-catalogs/`);
      setScopes(res.data?.data || []);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching scope catalogs:", error);

      Swal.fire(
        "Error",
        "Failed to load scope catalogs",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This scope catalog entry will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE}/scope-catalogs/${id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Scope catalog entry has been removed.",
        timer: 2000,
        showConfirmButton: false,
      });

      fetchScopes();
    } catch (error) {
      console.error("Error deleting scope catalog:", error);

      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to delete entry",
        "error"
      );
    }
  };

  const handleEdit = (item) => {
    navigate(`/catalog/edit-scope-catalog/${item.id}`);
  };

  const filteredScopes = scopes.filter(
    (item) =>
      item.scope_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      item.code
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      industryMap[item.industry]
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      item.competency_focus
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredScopes.length / entriesPerPage);

  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;

  const currentScopes = filteredScopes.slice(startIndex, endIndex);

  const startEntry =
    filteredScopes.length === 0 ? 0 : startIndex + 1;

  const endEntry = Math.min(
    endIndex,
    filteredScopes.length
  );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, "...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
    }

    return pages;
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="scope-table-page">
          <div className="scope-table-header">
            <div className="scope-table-header-left">
              <div>
                <h2>Scope Catalog Management</h2>
                
              </div>

              <span className="scope-table-count">
                {scopes.length} scopes
              </span>
            </div>

            <div className="scope-table-header-right">
              <div className="scope-table-search">
                <FiSearch className="scope-table-search-icon" />

                <input
                  type="text"
                  placeholder="Search scopes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="scope-table-refresh-btn"
                onClick={fetchScopes}
                title="Refresh"
              >
                <FiRefreshCw />
              </button>

              <button
                type="button"
                className="scope-table-add-btn"
                onClick={() =>
                  navigate("/catalog/add-scope-catalog")
                }
              >
                <FiPlus />
                Add Scope
              </button>
            </div>
          </div>

          {loading ? (
            <div className="scope-table-loader-container">
              <div className="scope-table-loader">
                <div className="scope-table-spinner"></div>
                <p>Loading scope catalogs...</p>
              </div>
            </div>
          ) : (
            <div className="scope-table-wrapper">
             

              <div className="scope-table-container">
                <table className="scope-table">
                  <thead>
                    <tr>
                      <th>Scope Name</th>
                      <th>Code</th>
                      <th>Industry</th>
                      <th>Competency Focus</th>
                      <th>Status</th>
                      <th>Active</th>
                      <th className="scope-table-actions-header">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentScopes.length > 0 ? (
                      currentScopes.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div className="scope-table-name-cell">
                              <div className="scope-table-avatar">
                                {item.scope_name
                                  ?.charAt(0)
                                  .toUpperCase()}
                              </div>

                              <span>{item.scope_name}</span>
                            </div>
                          </td>

                          <td>
                            <span className="scope-table-code-badge">
                              {item.code || "-"}
                            </span>
                          </td>

                          <td>
                            {industryMap[item.industry] || "-"}
                          </td>

                          <td>
                            {item.competency_focus || "-"}
                          </td>

                          <td>
                            <span className="scope-table-status-badge">
                              {item.status || "-"}
                            </span>
                          </td>

                          <td>
                            <span
                              className={`scope-table-active-status ${
                                item.is_active
                                  ? "scope-table-active"
                                  : "scope-table-inactive"
                              }`}
                            >
                              <span className="scope-table-status-dot"></span>

                              {item.is_active
                                ? "ACTIVE"
                                : "INACTIVE"}
                            </span>
                          </td>

                          <td className="scope-table-actions-cell">
                            <button
                              type="button"
                              className="scope-table-edit-btn"
                              onClick={() => handleEdit(item)}
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>

                            <button
                              type="button"
                              className="scope-table-delete-btn"
                              onClick={() =>
                                handleDelete(item.id)
                              }
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="7"
                          className="scope-table-no-data"
                        >
                          <div className="scope-table-empty-state">
                            <div className="scope-table-empty-icon">
                              <FiSearch />
                            </div>

                            <p>
                              No scope catalog entries found
                            </p>

                            <span>
                              {searchTerm
                                ? "Try adjusting your search"
                                : "Add your first scope to get started"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              

              {filteredScopes.length > 0 && (

                
                <div className="scope-table-pagination">
                   <div className="scope-table-top-controls">
                <div className="scope-table-show-entries">
                  <span>Show</span>

                  <select
                    value={entriesPerPage}
                    onChange={(e) =>
                      setEntriesPerPage(Number(e.target.value))
                    }
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>

                  <span>entries</span>
                </div>
              </div>
                  <div className="scope-table-pagination-info">
                    Showing{" "}
                    <strong>{startEntry}</strong> to{" "}
                    <strong>{endEntry}</strong> of{" "}
                    <strong>{filteredScopes.length}</strong>{" "}
                    entries
                  </div>

                  <div className="scope-table-pagination-controls">
                    <button
                      type="button"
                      className="scope-table-page-btn scope-table-prev-next"
                      disabled={currentPage === 1}
                      onClick={() =>
                        handlePageChange(currentPage - 1)
                      }
                    >
                      <FiChevronLeft />
                      Previous
                    </button>

                    <div className="scope-table-page-numbers">
                      {getPageNumbers().map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`dots-${index}`}
                            className="scope-table-page-dots"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            type="button"
                            key={page}
                            className={`scope-table-page-btn ${
                              currentPage === page
                                ? "scope-table-page-active"
                                : ""
                            }`}
                            onClick={() =>
                              handlePageChange(page)
                            }
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      type="button"
                      className="scope-table-page-btn scope-table-prev-next"
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        handlePageChange(currentPage + 1)
                      }
                    >
                      Next
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScopeCatalog;